<template>
  <qtm-icon-btn
    icon="mdi-brain"
    :loading="loading"
    title="Analyze"
    @click.prevent="analyze"
  />
</template>

<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid'
import { DateTime } from '@quotetome/materials-api'
import type { Attachment, Company, LineItem } from '@quotetome/materials-api'

export interface Props {
  attachment: Attachment
  company?: Company
  items?: LineItem[]
  supplierReference?: string
  units?: any[]
}

const props = withDefaults(defineProps<Props>(), {
  company: undefined,
  items: () => [],
  supplierReference: '',
  units: () => []
})
const emit = defineEmits(['analyzing', 'update:date-issued', 'update:items', 'update:supplier-reference'])

const loading = ref(false)
const unit = computed(() => props.units.find(u => u.is_default)?.code || 'Each')

let analyzeTimeout: ReturnType<typeof setTimeout>

onBeforeUnmount(() => clearTimeout(analyzeTimeout))

const { $api, $error } = useNuxtApp()

const analyze = async () => {
  emit('analyzing', props.attachment)

  loading.value = true
  try {
    const results = await $api.v1.attachments.analysis(
      props.attachment.id,
      {
        async: true,
        fallback: 'openai',
        null_quantity: true,
        taxes: props.company?.scan_taxes_as_items || undefined,
      },
    )

    if (results.status === 'pending') {
      analyzeTimeout = setTimeout(analyze, 5000)
      return
    }

    parseResults(results)
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}

const parseResults = (results: any) => {
  const items = results.items.map((item: any) => {
    let description = item.description

    if (props.company?.scan_items_sku && item.product_code && !item.description.includes(item.product_code)) {
      description = `${item.product_code} | ${description}`
    }

    return { ...item, id: uuidv4(), description, unit: unit.value }
  })

  if (results.invoice_number) {
    emit('update:supplier-reference', results.invoice_number)
  }

  if (results.invoice_date) {
    const parsedDate = Date.parse(results.invoice_date)

    if (!Number.isNaN(parsedDate)) {
      const date = new DateTime(new Date(parsedDate))

      emit('update:date-issued', date)
    }
  }

  emit('update:items', props.items.concat(items))
}
</script>
